import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Collapse,
  Stack,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  LocationOn as LocationIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import LinkifiedText from './LinkifiedText';

// Mobile-optimized inventory item card
const InventoryItemCard = ({ item, onEdit, onArchive, onUnarchive, isAdmin }) => {
  const [expanded, setExpanded] = React.useState(false);

  const handleToggleExpand = () => {
    setExpanded(!expanded);
  };

  const getAvailabilityColor = (available, total) => {
    const ratio = available / total;
    if (ratio > 0.5) return 'success';
    if (ratio > 0.2) return 'warning';
    return 'error';
  };

  return (
    <Card sx={{ mb: 2, boxShadow: 2 }}>
      <CardContent sx={{ pb: expanded ? 2 : 1 }}>
        {/* Primary Info - Always Visible */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="div" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
              {item.item_description || item.description || [item.manufacturer, item.model].filter(p => p && p.trim()).join(' ') || 'Equipment Item'}
            </Typography>
            {item.barcode && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Barcode: {item.barcode}
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={handleToggleExpand}
            aria-label={expanded ? "show less" : "show more"}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        {/* Key Metrics - Always Visible */}
        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
          <Chip
            size="small"
            icon={<InventoryIcon sx={{ fontSize: '1rem' }} />}
            label={`${item.total_quantity || item.totalQuantity || item.quantity || 0} total`}
            color={getAvailabilityColor(item.available_quantity || item.availableQuantity || item.quantity || 0, item.total_quantity || item.totalQuantity || item.quantity || 1)}
          />
          <Chip
            size="small"
            icon={<LocationIcon sx={{ fontSize: '1rem' }} />}
            label={item.warehouse_name || item.warehouse || 'No warehouse'}
            variant="outlined"
          />
          {item.archived_at && (
            <Chip
              size="small"
              label="Archived"
              color="default"
              variant="filled"
            />
          )}
        </Stack>

        {/* Expanded Details */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
            <Stack spacing={1}>
              {item.manufacturer && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Manufacturer:</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {item.manufacturer}
                  </Typography>
                </Box>
              )}
              {item.model && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Model:</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {item.model}
                  </Typography>
                </Box>
              )}
              {item.serial_number && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Serial Number:</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {item.serial_number}
                  </Typography>
                </Box>
              )}
              {(item.warehouse_name || item.warehouse) && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Warehouse:</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {item.warehouse_name || item.warehouse}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Available:</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {item.available_quantity || item.availableQuantity || item.quantity || 0}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Assigned:</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {item.assigned_quantity || 0}
                </Typography>
              </Box>
              {(item.zone_name || item.zone) && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Zone:</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {item.zone_name || item.zone}
                  </Typography>
                </Box>
              )}
              {item.notes && (
                <Box>
                  <Typography variant="body2" color="text.secondary">Notes:</Typography>
                  <LinkifiedText 
                    text={item.notes} 
                    variant="body2" 
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              )}
              
              {/* Action Buttons */}
              {isAdmin && (
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => onEdit(item)}
                    fullWidth
                  >
                    Edit
                  </Button>
                  {item.archived_at ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      startIcon={<UnarchiveIcon />}
                      onClick={() => onUnarchive(item.id)}
                      fullWidth
                    >
                      Unarchive
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      startIcon={<ArchiveIcon />}
                      onClick={() => onArchive(item.id)}
                      fullWidth
                    >
                      Archive
                    </Button>
                  )}
                </Stack>
              )}
            </Stack>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

// Mobile-optimized inventory summary component
const InventorySummaryMobile = ({
  inventory,
  searchQuery,
  onSearchChange,
  onEdit,
  onArchive,
  onUnarchive,
  isAdmin,
  showArchived,
  onShowArchivedChange,
  loading
}) => {
  const [debugOpen, setDebugOpen] = useState(false);
  
  const handleClearLocalStorage = () => {
    localStorage.removeItem('inventory_items');
    // Clear all bavs_ prefixed items
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('bavs_')) {
        localStorage.removeItem(key);
      }
    });
    setDebugOpen(false);
    window.location.reload();
  };
  
  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Typography 
        variant="h5" 
        sx={{ mb: 2, fontWeight: 600 }}
      >
        Inventory Summary
      </Typography>
      
      {/* Search and Filter Controls */}
      <Stack spacing={2} sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search items..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.paper',
            }
          }}
        />
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          {isAdmin && (
            <Button
              size="small"
              variant={showArchived ? 'contained' : 'outlined'}
              onClick={() => onShowArchivedChange(!showArchived)}
              startIcon={<ArchiveIcon />}
            >
              {showArchived ? 'Hide' : 'Show'} Archived
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => setDebugOpen(true)}
          >
            Debug
          </Button>
        </Box>
      </Stack>

      {/* Inventory Items */}
      {loading ? (
        <Typography variant="body2" color="text.secondary" align="center">
          Loading inventory...
        </Typography>
      ) : inventory.length === 0 ? (
        <Typography variant="body2" color="text.secondary" align="center">
          No items found
        </Typography>
      ) : (
        <Box>
          {inventory.map((item) => (
            <InventoryItemCard
              key={item.id}
              item={item}
              onEdit={onEdit}
              onArchive={onArchive}
              onUnarchive={onUnarchive}
              isAdmin={isAdmin}
            />
          ))}
        </Box>
      )}
      
      {/* Debug Dialog */}
      <Dialog open={debugOpen} onClose={() => setDebugOpen(false)}>
        <DialogTitle>Debug Tools</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Clear all locally stored inventory data? This will force a fresh sync from the database.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Note: This only affects data cached on this device.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDebugOpen(false)}>Cancel</Button>
          <Button onClick={handleClearLocalStorage} color="error" variant="contained">
            Clear Local Storage
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventorySummaryMobile;